import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ImageUploader from "@/components/ImageUploader";
import { toast } from "sonner";
import { RefreshCw, Send, CheckCircle2, AlertTriangle, ExternalLink, Pencil } from "lucide-react";

const fmt = (iso) => { try { return iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—"; } catch { return "—"; } };
const EMPTY = { name: "", description: "", category_id: "", city: "", email: "", phone: "", website: "", logo: "", cover_image: "" };

export default function AdminFicheReminders() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendingSel, setSendingSel] = useState(false);
  const [selected, setSelected] = useState([]);   // ids cochés
  const [cats, setCats] = useState([]);
  const [globalStats, setGlobalStats] = useState(null);   // { total, complete, rate }
  const [editing, setEditing] = useState(null);    // id en cours d'édition
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/partners/fiche-reminders"); setRows(data || []); setSelected([]); }
    catch { setRows([]); }
    finally { setLoading(false); }
    try {
      const { data } = await api.get("/admin/partners/fiches");
      const total = (data || []).length;
      const complete = (data || []).filter((f) => f.complete).length;
      setGlobalStats({ total, complete, rate: total ? Math.round((complete / total) * 100) : 0 });
    } catch { setGlobalStats(null); }
  };
  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCats(r.data || [])).catch(() => setCats([]));
  }, []);

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/partners/fiche-reminder");
      toast.success(`Relances : ${data.sent} envoyée(s), ${data.skipped_recent} en attente (dégressif), ${data.skipped_done} terminées`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'envoi."); }
    finally { setSending(false); }
  };

  // Partenaires sélectionnables : fiche encore incomplète avec un email
  const selectable = rows.filter((r) => !r.complete && r.email);
  const allChecked = selectable.length > 0 && selectable.every((r) => selected.includes(r.id));
  const toggleOne = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleAll = () => setSelected(allChecked ? [] : selectable.map((r) => r.id));

  const sendSelected = async () => {
    if (!selected.length) { toast.error("Sélectionnez au moins un partenaire."); return; }
    setSendingSel(true);
    try {
      const { data } = await api.post("/admin/partners/fiche-reminder-selected", { ids: selected });
      toast.success(`Relance ciblée : ${data.sent} envoyée(s)${data.failed ? `, ${data.failed} échec(s)` : ""}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'envoi."); }
    finally { setSendingSel(false); }
  };

  const openEdit = (r) => {
    setEditing(r.id);
    setForm({
      name: r.name || "", description: r.description || "", category_id: r.category_id || "",
      city: r.city || "", email: r.email || "", phone: r.phone || "", website: r.website || "",
      logo: r.logo || "", cover_image: r.cover_image || "",
    });
  };
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/businesses/${editing}/fiche`, form);
      toast.success("Fiche mise à jour");
      setEditing(null);
      await load();
      window.dispatchEvent(new Event("admin:todos-refresh"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); }
    finally { setSaving(false); }
  };

  const completed = rows.filter((r) => r.completed_after_reminder).length;
  const pending = rows.filter((r) => !r.complete).length;
  const monthlyPhase = rows.filter((r) => !r.complete && (r.reminder_count || 0) >= 4).length;

  return (
    <div data-testid="admin-fiche-reminders">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Relances fiche</h1>
          <p className="text-sm text-slate-500 mt-1">Partenaires à fiche incomplète (sans nom, description, image, catégorie, ville ou email) et suivi des relances dégressives (J+1, J+3, J+7).</p>
        </div>
        <Button onClick={sendNow} disabled={sending} className="btn-shine text-white" data-testid="admin-fiche-send">
          <Send size={14} className={`mr-1.5 ${sending ? "animate-pulse" : ""}`}/> {sending ? "Envoi…" : "Envoyer les relances maintenant"}
        </Button>
      </div>

      <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 flex items-start gap-2" data-testid="fiche-auto-banner">
        <CheckCircle2 size={16} className="mt-0.5 shrink-0"/>
        <span>Relance automatique activée : chaque jour à 9h30, les partenaires à fiche incomplète reçoivent un rappel (J+1, J+3 puis J+7). Le compteur « Complétées après relance » ci-dessous suit ceux qui ont finalisé leur fiche suite à ces rappels.</span>
      </div>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-2xl font-bold text-slate-900">{rows.length}</div><div className="text-xs text-slate-500">Partenaires suivis</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-2xl font-bold text-amber-600">{pending}</div><div className="text-xs text-slate-500">Fiches encore incomplètes</div></div>
        <div className="bg-white border border-violet-200 rounded-xl p-4" data-testid="fiche-monthly-phase"><div className="text-2xl font-bold text-violet-700">{monthlyPhase}</div><div className="text-xs text-slate-500">En relance mensuelle (&gt; J+7)</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-2xl font-bold text-emerald-600">{completed}</div><div className="text-xs text-slate-500">Complétées après relance</div></div>
        <div className="bg-white border border-emerald-200 rounded-xl p-4" data-testid="fiche-global-rate">
          <div className="flex items-baseline gap-1">
            <div className="text-2xl font-bold text-emerald-700">{globalStats ? `${globalStats.rate}%` : "—"}</div>
            {globalStats && <div className="text-xs text-slate-400">({globalStats.complete}/{globalStats.total})</div>}
          </div>
          <div className="text-xs text-slate-500">Taux de complétion global</div>
          <div className="mt-2 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${globalStats ? globalStats.rate : 0}%` }}/>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm text-slate-500" data-testid="fiche-selected-count">
          {selected.length > 0 ? `${selected.length} partenaire(s) sélectionné(s)` : "Cochez des partenaires pour une relance ciblée (envoi immédiat)."}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={sendSelected} disabled={sendingSel || selected.length === 0} className="btn-shine text-white" data-testid="admin-fiche-send-selected">
            <Send size={14} className={`mr-1.5 ${sendingSel ? "animate-pulse" : ""}`}/> {sendingSel ? "Envoi…" : `Relancer la sélection${selected.length ? ` (${selected.length})` : ""}`}
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="admin-fiche-reload"><RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`}/> Actualiser</Button>
        </div>
      </div>

      <div className="mt-2 bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 w-10"><Checkbox checked={allChecked} onCheckedChange={toggleAll} disabled={selectable.length === 0} data-testid="fiche-select-all" aria-label="Tout sélectionner"/></th>
                <th className="text-left px-4 py-3">Partenaire</th>
                <th className="text-left px-4 py-3">Ville</th>
                <th className="text-center px-4 py-3">Relances</th>
                <th className="text-left px-4 py-3">Dernier envoi</th>
                <th className="text-left px-4 py-3">Éléments manquants</th>
                <th className="text-left px-4 py-3">État</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Chargement…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Aucune fiche à relancer. 🎉</td></tr>
              ) : rows.map((r) => {
                const canSelect = !r.complete && !!r.email;
                return (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`fiche-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <Checkbox checked={selected.includes(r.id)} onCheckedChange={() => toggleOne(r.id)} disabled={!canSelect} data-testid={`fiche-select-${r.id}`} aria-label={`Sélectionner ${r.name || ""}`}/>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{r.name || "—"}</div>
                    <div className="text-xs text-slate-400">{r.email || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{r.city || "—"}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="font-semibold">{r.reminder_count}</div>
                    {!r.complete && r.reminder_count >= 4 && (
                      <span className="mt-1 inline-block text-[10px] px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-100" data-testid={`fiche-phase-${r.id}`}>Mensuelle &gt; J+7</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{fmt(r.last_sent)}</td>
                  <td className="px-4 py-3">
                    {r.missing.length === 0 ? <span className="text-xs text-slate-400">—</span> :
                      <div className="flex flex-wrap gap-1">{r.missing.map((m) => <span key={m} className="text-[11px] px-1.5 py-0.5 rounded bg-red-50 text-red-600 border border-red-100">{m}</span>)}</div>}
                  </td>
                  <td className="px-4 py-3">
                    {r.complete ? (
                      <span className="inline-flex flex-col gap-0.5" data-testid={`fiche-complete-${r.id}`}>
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                          <CheckCircle2 size={14}/> {r.completed_after_reminder ? "Complétée depuis relance" : "Complète"}
                        </span>
                        {r.completed_at && <span className="text-[11px] text-slate-400">le {fmt(r.completed_at)}</span>}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600"><AlertTriangle size={14}/> Incomplète</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <a href={`/commerce/${r.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#E05638] mr-3" data-testid={`fiche-view-${r.id}`}><ExternalLink size={13}/> Voir</a>
                    <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`fiche-edit-${r.id}`}>
                      <Pencil size={13} className="mr-1"/> Modifier
                    </Button>
                  </td>
                </tr>);
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="fiche-rem-edit-dialog">
          <DialogHeader><DialogTitle>Modifier la fiche partenaire</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Nom</label>
              <Input value={form.name} onChange={set("name")} data-testid="fiche-rem-edit-name"/>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Catégorie principale</label>
              <Select value={form.category_id} onValueChange={(v) => setForm((s) => ({ ...s, category_id: v }))}>
                <SelectTrigger data-testid="fiche-rem-edit-category"><SelectValue placeholder="Choisir une catégorie"/></SelectTrigger>
                <SelectContent>{cats.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600">Ville</label>
                <Input value={form.city} onChange={set("city")} data-testid="fiche-rem-edit-city"/>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Email</label>
                <Input value={form.email} onChange={set("email")} data-testid="fiche-rem-edit-email"/>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600">Téléphone</label>
                <Input value={form.phone} onChange={set("phone")} data-testid="fiche-rem-edit-phone"/>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Site web</label>
                <Input value={form.website} onChange={set("website")} data-testid="fiche-rem-edit-website"/>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Description</label>
              <Textarea rows={4} value={form.description} onChange={set("description")} data-testid="fiche-rem-edit-desc"/>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Logo</label>
              <ImageUploader value={form.logo ? [form.logo] : []} max={1} testId="fiche-rem-edit-logo"
                             onChange={(arr) => setForm((s) => ({ ...s, logo: arr[0] || "" }))}/>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Image de couverture</label>
              <ImageUploader value={form.cover_image ? [form.cover_image] : []} max={1} testId="fiche-rem-edit-cover"
                             onChange={(arr) => setForm((s) => ({ ...s, cover_image: arr[0] || "" }))}/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving} data-testid="fiche-rem-edit-cancel">Annuler</Button>
            <Button onClick={save} disabled={saving} className="btn-shine text-white" data-testid="fiche-rem-edit-save">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
